/* eslint-disable no-restricted-syntax */

import appConfig from "config/app";
import { type GetBrandQuery } from "src/graphql-next/generated/types";
import { BrandCodeEnum } from "src/graphql/generated/types";
import { z } from "zod";
import { create } from "zustand";
import { persist } from "zustand/middleware";

type ExtendedBrand = {
  identity: {
    secondaryLogo: {
      colorUrl: string;
      whiteUrl: string;
    };
  };
  customerPortal: {
    name?: string;
    helpDesk?: {
      name: string;
    };
    marketplace?: Array<{
      url: string;
      quality: "CO_OWNER" | "LANDLORD" | "TENANT";
    }>;
    links: {
      legalNoticeUrl: string;
      securityInformationUrl: string;
      forgotLoginUrl: string;
      servicesUrl?: string;
    };
    emails?: {
      accessibility?: string;
    };
    faviconUrl: string;
  };
  dataProtectionPolicy: {
    formUrl: string;
  };
};

const extendedBrand: Record<"foncia" | "esset", ExtendedBrand> = {
  foncia: {
    identity: {
      secondaryLogo: {
        colorUrl: "/images/logo-foncia-color.png",
        whiteUrl: "/images/logo-foncia-white.png",
      },
    },
    customerPortal: {
      helpDesk: {
        name: "Foncia 24/7",
      },
      marketplace: [
        {
          url: "https://marketplace.foncia.com/?utm_source=myfoncia&utm_medium=referral&utm_campaign=FONC000461",
          quality: "CO_OWNER",
        },
        {
          url: "https://marketplace.foncia.com/?utm_source=myfoncia&utm_medium=referral&utm_campaign=FONC000462",
          quality: "LANDLORD",
        },
        {
          url: "https://marketplace.foncia.com/?utm_source=myfoncia&utm_medium=referral&utm_campaign=FONC000463",
          quality: "TENANT",
        },
      ],
      links: {
        legalNoticeUrl: "https://fr.foncia.com/mentions-legales",
        securityInformationUrl:
          "https://fr.foncia.com/notre-preoccupation-la-securite-de-vos-donnees",
        forgotLoginUrl:
          "https://fr.foncia.com/premiere-connexion-a-mon-espace-client-foncia",
        servicesUrl: "https://fr.foncia.com/services",
      },
      emails: {
        accessibility: "accessibilite@foncia.com",
      },
      faviconUrl: "/favicon-foncia",
    },
    dataProtectionPolicy: {
      formUrl:
        "https://emeria.adequacy-corporate.com/frm/index.php?_id=200&_entite=2",
    },
  },
  esset: {
    identity: {
      secondaryLogo: {
        colorUrl: "/images/logo-esset-color.png",
        whiteUrl: "/images/logo-esset-white.png",
      },
    },
    customerPortal: {
      name: "Esset\u00A0Connect",
      links: {
        legalNoticeUrl: "https://www.esset-pm.com/mentions-legales",
        securityInformationUrl: "https://www.esset-pm.com", // TODO: update url
        forgotLoginUrl: "https://www.esset-pm.com", // TODO: update url
      },
      emails: {
        accessibility: "accessibilite@esset-pm.com",
      },
      faviconUrl: "/favicon-esset", // TODO: update url
    },
    dataProtectionPolicy: {
      formUrl:
        "https://emeria.adequacy-corporate.com/frm/index.php?_id=200&_entite=2",
    },
  },
};

type BrandQuerySuccessData = Extract<
  GetBrandQuery["brand"],
  { __typename?: "QueryBrandSuccess" }
>["data"];

type CustomBrandData = Omit<
  BrandQuerySuccessData,
  "customerPortal" | "identity"
> & {
  identity: BrandQuerySuccessData["identity"] & ExtendedBrand["identity"];
  customerPortal: BrandQuerySuccessData["customerPortal"] &
    ExtendedBrand["customerPortal"];
  dataProtectionPolicy: BrandQuerySuccessData["dataProtectionPolicy"] &
    ExtendedBrand["dataProtectionPolicy"];
};

interface BrandState {
  brandCode: BrandCodeEnum;
  brand: CustomBrandData | undefined;
  setBrandCode: (code: BrandCodeEnum) => void;
  setBrand: (brand: BrandQuerySuccessData) => void;
}

export const useBrandStore = create<BrandState>()(
  persist(
    (set) => {
      const setBrandCode = (brandCode: BrandCodeEnum) => {
        set({ brandCode });
      };

      const setBrand = (data: BrandQuerySuccessData) => {
        const brand = {
          ...data,
          identity: {
            ...data.identity,
            ...extendedBrand[data.code as keyof typeof extendedBrand].identity,
          },
          customerPortal: {
            ...data.customerPortal,
            ...extendedBrand[data.code as keyof typeof extendedBrand]
              .customerPortal,
          },
          dataProtectionPolicy: {
            ...data.dataProtectionPolicy,
            ...extendedBrand[data.code as keyof typeof extendedBrand]
              .dataProtectionPolicy,
          },
        };
        set({ brand });
      };

      return {
        brandCode: appConfig.brand.code as BrandCodeEnum,
        brand: undefined,
        setBrandCode,
        setBrand,
      };
    },
    {
      name: "brand-storage",
      partialize: ({ brandCode }) => ({ brandCode }),
      merge: (persisted, current) => {
        if (appConfig.brand.enableThemeSwitcher !== "true") {
          return current;
        }

        const stored = z
          .object({ brandCode: z.enum(BrandCodeEnum) })
          .safeParse(persisted);

        return stored.success ? { ...current, ...stored.data } : current;
      },
    },
  ),
);
